import clsx from 'clsx';
import Img from '@/components/Img';
import styles from './ImageTextFeatures.module.scss';
import Button, { ButtonType } from '@/components/Button/Button';
import Text from '@/components/Text/Text';
import { mediaPath } from '@/mediaPath';

export interface ImageTextFeaturesProps {
  data: {
    title: string;
    image: {
      height: number,
      width: number,
      url: string,
      mime?: string;
    };
    imagePosition?: 'left' | 'right';
    button: {
      id: number,
      title: string,
      type: ButtonType,
      link: string
    }
    features: {
      id: number,
      icon: {
        id: number,
        alternativeText: string | null,
        width: number,
        height: number,
        url: string,
      }
      text: string;
    }[];
    imageSize?: 'small' | 'medium' | 'large';
  },
}

const ImageTextFeatures = ({ data }: ImageTextFeaturesProps) => {
  const isVideo = data.image?.mime?.startsWith('video');
  const mediaUrl = data.image?.url ? mediaPath(data.image.url) : null;

  return (
    <section className={clsx(styles.ImageTextFeatures, { [styles.left]: data.imagePosition === 'left' }, 'custom')}>
      <div className={styles.textContent}>
        {data.title && (
          <Text
            className={styles.title}
            tag='h3'
          >
            {data.title}
          </Text>
        )}
        <ul className={styles.feature_list}>
          {data.features.map((feature) => (
            <li
              className={styles.feature_card}
              key={feature.id}
            >
              <Img
                src={feature.icon.url}
                height={40}
                width={40}
                alt={feature.icon.alternativeText || ''}
              />
              <Text tag='h6'>{feature.text}</Text>
            </li>
          ))}
        </ul>
        {data.button && (
          <Button
            type={data.button.type}
            className={styles.primaryButton}
            iconName='arrow_forward'
            href={data.button.link}
          >
            {data.button.title}
          </Button>
        )}
      </div>
      {mediaUrl && (
        isVideo ? (
          <video
            key={mediaUrl}
            className={clsx(styles.media, styles[data.imageSize || 'medium'])}
            src={mediaUrl}
            autoPlay
            loop
            muted
            playsInline
            preload='auto'
          >
            <track kind='captions' />
            Your browser does not support the video tag.
          </video>
        ) : (
          <Img
            key={mediaUrl}
            src={data.image.url}
            className={clsx(styles.media, styles[data.imageSize || 'medium'])}
            height={data.image?.height}
            width={data.image?.width}
            alt={data.title || ''}
          />
        )
      )}
    </section>
  );
};

export default ImageTextFeatures;
